import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import cn from 'classnames';
import { withTranslation } from 'react-i18next';
import normalizeWheel from 'normalize-wheel';
import ReactTooltip from 'react-tooltip';
import Modal from 'react-modal';
import polylabel from '@mapbox/polylabel';
import { createSelector } from 'reselect';

import { setPopUp } from 'actions/environment';
import { ROOT_URL } from 'constants/Config';
import getApartments from 'constants/apartments';

import SpinnerImage from 'components/common/SpinnerImage';
import Filters from 'components/Building/Chess/Filters';
import About from '../About/About';

import styles from './Gallery.scss';

import zoomIcon from 'assets/images/icons/icon-zoom.svg';
import noFloor from 'assets/images/no-floor.png';
import iconLock from 'assets/images/icons/icon-lock.svg';
import iconClose from 'assets/images/icons/cross.svg';

import logoPrint from 'assets/images/logo-black.svg';
import i18n from '../../../../../i18n';
import { isReserved } from '../../../../../helper';
import { gaEvent, trackGA } from 'services/ga';

const getFloorName = (name) => {
  switch (name) {
    case 'M':
    case 'М':
      return i18n.t('components.building.rightMenu.attic');
    case 'Мансарда':
    case 'Парковка':
    case 'Цоколь':
    case 'Терраса':
      return name;
    default:
      return `${name} ${i18n.t('components.building.rightMenu.floor')}`;
  }
};

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const WHEEL_FACTOR = 1.05;
const CLICK_FACTOR = 1.5;

class ChessGalleryPopup extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hoverCellId: null,
      dragging: false,
      scale: 1,
      x: 0,
      y: 0,
      row: props.row,
      showFloorScroll: false,
    };
  }

  isNegative = (n) => ((n = +n) || 1 / n) < 0;

  handleMouseWheel = (e) => {
    e.preventDefault();
    const normalized = normalizeWheel(e);
    this.zoom(this.isNegative(normalized.pixelY), WHEEL_FACTOR);
  };

  zoom = (zoomIn, zoomFactor) => {
    const newScale = this.state.scale * (zoomIn ? zoomFactor : 1 / zoomFactor);

    this.setState({
      scale: Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale)),
    });
  };

  zoomIn = () => {
    this.handleTrackEvent({
      eventName: 'buildingChessFloorDialogButtonsClick',
      eventLabel: 'uvelichit',
    });

    this.zoom(true, CLICK_FACTOR);
  };

  zoomOut = () => {
    this.handleTrackEvent({
      eventName: 'buildingChessFloorDialogButtonsClick',
      eventLabel: 'umenshit',
    });

    this.zoom(false, CLICK_FACTOR);
  };

  handleZoomChange = (e) => {
    this.setState({
      scale: e.target.value,
    });
  };

  handleZoomMouseDown = (e) => {
    e.stopPropagation();
  };

  getZoomPercent = () => (this.state.scale - MIN_SCALE) / (MAX_SCALE - MIN_SCALE);

  componentDidMount() {
    document.addEventListener('mousemove', this.handleMouseMove, false);
    document.addEventListener('mouseup', this.handleMouseUp, false);
    document.addEventListener('touchmove', this.handleTouchMove, false);
    document.addEventListener('touchend', this.handleMouseUp, false);
    window.addEventListener('resize', this.adjustFloorScroll);
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleMouseMove, false);
    document.removeEventListener('mouseup', this.handleMouseUp, false);
    document.removeEventListener('touchmove', this.handleTouchMove, false);
    document.removeEventListener('touchend', this.handleMouseUp, false);
    window.removeEventListener('resize', this.adjustFloorScroll);
  }

  adjustFloorScroll = (e) => {
    this.setState({
      showFloorScroll: this.floors.clientHeight < this.floors.scrollHeight,
    });
  };

  handleTouchStart = (e) => {
    this.handleMouseDown(e.touches[0]);
  };

  handleMouseDown = (e) => {
    this.setState({ dragging: true });
    // Set coords
    this.coords = {
      x: e.pageX,
      y: e.pageY,
    };
  };

  handleMouseUp = () => {
    this.setState({ dragging: false });
    this.coords = {};
  };

  handleTouchMove = (e) => {
    this.handleMove(e.touches[0]);
  };

  handleMouseMove = (e) => {
    if (!this.state.dragging) {
      return;
    }
    e.preventDefault();
    this.handleMove(e);
  };

  handleMove = (e) => {
    const xDiff = this.coords.x - e.pageX;
    const yDiff = this.coords.y - e.pageY;
    // Update to our new coordinates
    this.coords.x = e.pageX;
    this.coords.y = e.pageY;

    this.setState({
      x: this.state.x - xDiff,
      y: this.state.y - yDiff,
    });
  };

  handleTrackEvent = ({ eventName, eventLabel }) => {
    const { profile, objectType, building, isTable } = this.props;
    const eventContext = isTable ? 'shahmatka' : 'spisok';

    trackGA(
      gaEvent[eventName]({
        eventLabel,
        eventContext,
        productType: objectType,
        productName: building.title,
        productID: building.id,
        user_id: profile.id,
      })
    );
  };

  renderVectorOverlay({ all = false } = {}, rowData) {
    const { t, sectionCells, data: selected, currency } = this.props;
    const data = rowData;
    const apartments = getApartments(t);
    if (data.pointers) {
      const [width, height] = data.pointers.split(':');
      return (
        <div className={styles.Plan}>
          <div className="lol">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height={height}
              width={width}
              viewBox={`0 0 ${width} ${height}`}
            >
              <g>
                {(all ? sectionCells[this.state.row].cells : [data])
                  .filter((cell) => cell && !cell.hidden && cell.pointers)
                  .map((cell) => {
                    const coordinates = cell.pointers.split(':')[2];
                    const cs = coordinates.split(',');
                    const path = cs
                      .reduce((path, c, i) => path.concat(i % 2 === 1 ? `${c} L ` : `${c} `), 'M ')
                      .replace(/L $/, 'Z');
                    return [
                      <path
                        className={cn(styles.Flat, {
                          [styles.SelectedFlat]: cell.id === selected.id,
                          [styles.FilteredFlat]: cell.mark === false,
                        })}
                        d={path}
                        key={cell.id}
                        onMouseEnter={() => {
                          this.setState({ hoverCellId: cell.id });
                        }}
                        onMouseLeave={() => {
                          if (this.state.hoverCellId === cell.id) {
                            this.setState({ hoverCellId: null });
                          }
                        }}
                        onClick={this.props.handleCellClick.bind(this, cell.id, [
                          cell.row,
                          cell.col,
                        ])}
                      />,
                    ];
                  })}
              </g>
            </svg>
            {all &&
              sectionCells[this.state.row].cells
                .filter((cell) => cell && !cell.hidden && cell.pointers)
                .map((cell) => {
                  const coordinates = cell.pointers.split(':')[2];
                  const cs = coordinates.split(',');

                  const geo = cs
                    .reduce((geo, val, i) => {
                      if (i % 2 === 0) {
                        return [...geo, [~~cs[i], ~~cs[i + 1]]];
                      }
                      return geo;
                    }, [])
                    .concat([[~~cs[0], ~~cs[1]]]);

                  const center = polylabel([geo], 20.0);

                  return (
                    <div
                      key={cell.id}
                      className={cn(styles.FlatPopup, {
                        [styles.FlatHover]: cell.id === this.state.hoverCellId,
                        [styles.Reservation]: isReserved(cell.data.status),
                        [styles.Locked]: cell.data.status === 'fundStatus',
                        [styles.Promo]: cell.data.view_action === '1',
                      })}
                      style={{
                        left: `${(center[0] * 100) / width}%`,
                        top: `${(center[1] * 100) / height}%`,
                        transform: `scale(${1 / this.state.scale})`,
                      }}
                    >
                      {cell.data.status === 'fundStatus' ? (
                        <div className={styles.FlatPopupInner}>
                          <img src={iconLock} alt="" className={styles.iconFund} />
                        </div>
                      ) : (
                        <div className={styles.FlatPopupInner}>
                          <div className={styles.FlatNumber}>
                            <span>№{cell.data.number}</span>
                          </div>
                          <div className={styles.FlatArea}>
                            <span>
                              {t('components.building.rightMenu.area', { area: cell.data.area })}
                            </span>
                          </div>
                          <div className={styles.FlatCost}>
                            <span>
                              {cell.data.costFormatted} {currency}
                            </span>
                          </div>
                          <div className={styles.FlatType}>
                            <span>{apartments[cell.data.cnt_apart]}</span>
                          </div>
                          <div className={styles.FlatAreaAndPrice}>
                            <span>
                              {t('components.building.rightMenu.area', { area: cell.data.area })},{' '}
                              {cell.data.priceFormatted}{' '}
                              {t('components.building.rightMenu.priceForM2', { currency })}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
          </div>
        </div>
      );
    }
  }

  handleFloorClick = (row) => {
    this.handleTrackEvent({
      eventName: 'buildingChessLotAboutButtonsClick',
      eventLabel: 'etazh',
    });

    this.setState({
      row,
    });
  };

  handleNextFloorClick = (row) => {
    this.floors.scrollTop += this.floors.clientHeight;
  };

  handlePrevFloorClick = (row) => {
    this.floors.scrollTop -= this.floors.clientHeight;
  };

  renderRow = ({ name }, i) => {
    const disabled =
      this.props.sectionCells[i].cells.filter(
        (cell) => cell && !cell.hidden && cell.pointers && cell.mark !== false
      ).length === 0;
    return (
      <div
        key={i}
        className={cn(styles.FloorItem, {
          [styles.Active]: i === this.state.row,
          [styles.Light]: disabled,
        })}
        onClick={this.handleFloorClick.bind(this, i)}
      >
        {' '}
        {name}{' '}
      </div>
    );
  };

  getFloorsRef = (ref) => {
    this.floors = ref;
    if (this.floors) {
      this.adjustFloorScroll();
    }
  };

  render() {
    const {
      building,
      data: room,
      rows,
      client,
      sectionId,
      filters,
      pavilionsFlat,
      handleFilters,
      sectionCells,
      cells,
      isClientView,
      t,
      profile,
      objectType,
      isTable,
    } = this.props;
    const rowCells = sectionCells[this.state.row].cells.filter((cell) => cell && cell.pointers);
    const [width, height] = ((rowCells[0] && rowCells[0].pointers) || '0:0').split(':');

    const { scale, x, y } = this.state;

    return (
      <div className={styles.PopUpOuter}>
        <div className={styles.PopUpInner}>
          {/* <div className={styles.TopInfo}> */}
          {/* <span>{building.title} / {getFloorName(room.data.floorName)}</span> */}
          {/* </div> */}

          <div className={styles.FloorPlanWrapper}>
            <div className={styles.FloorPlanTop}>
              {!isClientView && (
                <Filters
                  pavilions={building.pavilions}
                  pavilionsFlat={pavilionsFlat}
                  filters={filters}
                  handleFilters={handleFilters}
                  search={filters}
                  chessfilter="true"
                  cells={cells}
                  profile={profile}
                  buildingData={building}
                  objectType={objectType}
                  isTable={isTable}
                />
              )}
              <div className={styles.Close} onClick={this.props.onClose}>
                <img src={iconClose} alt="close" />
              </div>
            </div>
            <div className={styles.FloorPlanLeft}>
              <div className={styles.SelectFloor}>
                {this.state.showFloorScroll && (
                  <div
                    className={cn(styles.SelectFloorArrow, styles.Top)}
                    onClick={this.handlePrevFloorClick}
                  />
                )}
                <div className={styles.Floors} ref={this.getFloorsRef}>
                  {rows.map(this.renderRow)}
                </div>
                {this.state.showFloorScroll && (
                  <div
                    className={cn(styles.SelectFloorArrow, styles.Bottom)}
                    onClick={this.handleNextFloorClick}
                  />
                )}
              </div>
            </div>
            {!isClientView && (
              <div className={styles.FloorPlanRight}>
                <About
                  data={room}
                  building={building}
                  client={client}
                  sectionId={sectionId}
                  withImage={false}
                  chessfilter
                />
              </div>
            )}
            <div
              className={styles.FloorPlan}
              onWheel={this.handleMouseWheel}
              onMouseDown={rowCells[0] ? this.handleMouseDown : (_) => _}
              onTouchStart={rowCells[0] ? this.handleTouchStart : (_) => _}
              style={{
                cursor: this.state.dragging ? '-webkit-grabbing' : 'default',
              }}
            >
              {/* <Loader active={this.props.isChessLoading}/> */}
              <div className={styles.FloorTitle}>
                {rows[this.state.row].name} {t('components.building.rightMenu.floor')}
              </div>
              {!!rowCells[0] && (
                <div className={styles.ZoomButtons}>
                  <div onClick={this.zoomOut} className={cn(styles.ZoomBtn, styles.ZoomOut)}></div>
                  <input
                    type="range"
                    value={this.state.scale}
                    step={0.01}
                    min={MIN_SCALE}
                    max={MAX_SCALE}
                    onChange={this.handleZoomChange}
                    onMouseDown={this.handleZoomMouseDown}
                  />
                  <div onClick={this.zoomIn} className={cn(styles.ZoomBtn, styles.ZoomIn)}></div>
                </div>
              )}
              <SpinnerImage
                key={this.state.row}
                className={styles.ImageInfo}
                imageSrc={`${ROOT_URL}/image/plan?id=${
                  rowCells[0] ? rowCells[0].planId : null
                }&preset=4000`}
                imageAlt="Room"
                spinner={<div className={styles.LoaderInfo} />}
                blur={this.props.isChessLoading}
                placeholder={
                  <div className={styles.PlaceholderOuter}>
                    <img className={styles.PlaceholderImage} src={noFloor} alt="Not found" />
                    <div className={styles.PlaceholderText}>
                      {t('components.building.rightMenu.emptyPlanning')}
                    </div>
                  </div>
                }
                style={
                  rowCells[0]
                    ? {
                        ...(height &&
                          width && {
                            width: `${Math.min(69, (54 * width) / height)}vmin`,
                            height: `${Math.min(54, (69 * height) / width)}vmin`,
                            transform: `translate(${x}px, ${y}px) scale(${scale})`,
                          }),
                        ...(this.state.dragging && { transition: '0s' }),
                      }
                    : !this.props.isClientView
                    ? {
                        transition: '0s',
                        left: '-280px',
                      }
                    : {
                        transition: '0s',
                      }
                }
              >
                {rowCells[0] &&
                  !this.props.isClientView &&
                  this.renderVectorOverlay({ all: true }, rowCells[0])}
              </SpinnerImage>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export const ChessGalleryPopupWithRouter = withRouter(ChessGalleryPopup);

/** **********   ************ */

class Gallery extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoaded: false,
    };

    this.handleImageLoad = this.handleImageLoad.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.data.planId !== nextProps.data.planId) {
      this.setState({ isLoaded: false });
    }
  }

  renderVectorOverlay({ all = false } = {}) {
    const { row, data, sectionCells } = this.props;
    if (data.pointers) {
      const [width, height] = data.pointers.split(':');
      return (
        <div className={styles.Plan}>
          <div className="lol">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height={height}
              width={width}
              viewBox={`0 0 ${width} ${height}`}
            >
              <g>
                {(all ? sectionCells[row].cells : [data])
                  .filter((cell) => cell && cell.pointers)
                  .map((cell) => {
                    const coordinates = cell.pointers.split(':')[2];
                    const cs = coordinates.split(',');
                    const path = cs
                      .reduce((path, c, i) => path.concat(i % 2 === 1 ? `${c} L ` : `${c} `), 'M ')
                      .replace(/L $/, 'Z');
                    return [
                      <path
                        className={cn(styles.Flat, { [styles.SelectedFlat]: cell.id === data.id })}
                        d={path}
                        key={cell.id}
                        onMouseEnter={() => {
                          this.setState({ hoverCellId: cell.id });
                        }}
                        onMouseLeave={() => {
                          if (this.state.hoverCellId === cell.id) {
                            this.setState({ hoverCellId: null });
                          }
                        }}
                      />,
                    ];
                  })}
              </g>
            </svg>
          </div>
        </div>
      );
    }
  }

  setPopUp = () => {
    this.handleTrackEvent({
      eventName: 'buildingChessLotAboutButtonsClick',
      eventLabel: 'uvelichit_planirovku_etazha',
    });
    this.setState({
      showPopup: true,
    });
  };

  handleClose = () => {
    this.handleTrackEvent({
      eventName: 'buildingChessFloorDialogButtonsClick',
      eventLabel: 'zakryt',
    });

    this.setState({
      showPopup: false,
    });
  };

  handleImageLoad() {
    this.setState({ isLoaded: true }, ReactTooltip.rebuild);
    if (this.props.handleImageLoad) {
      this.props.handleImageLoad();
    }
  }

  getRanges = (array) => {
    let ranges = [],
      rstart,
      rend;
    for (let i = 0; i < array.length; i++) {
      rstart = array[i];
      rend = rstart;
      while (array[i + 1] - array[i] === 1) {
        rend = array[i + 1]; // increment the index if the numbers sequential
        i++;
      }
      ranges.push(rstart === rend ? `${rstart}` : `${rstart} — ${rend}`);
    }
    return ranges;
  };

  handleTrackEvent = ({ eventName, eventLabel }) => {
    const { profile, objectType, building, isTable } = this.props;
    const eventContext = isTable ? 'shahmatka' : 'spisok';

    trackGA(
      gaEvent[eventName]({
        eventLabel,
        eventContext,
        productType: objectType,
        productName: building.title,
        productID: building.id,
        user_id: profile.id,
      })
    );
  };

  render() {
    const { data: room, client, print, pavilionName, sectionName, t, currency } = this.props;
    const { isLoaded } = this.state;
    return (
      <div className={cn(styles.Gallery, { [styles.Client]: client }, { [styles.Print]: print })}>
        <div className={styles.Slider}>
          {/* <AngleUp className={styles.MoveLeft} /> */}
          {print && (
            <div className={styles.PrintHeader}>
              <div className={styles.PrintHeaderLeft}>
                <div className={styles.FloorNumberPrint}>
                  {print
                    ? `${t('components.building.rightMenu.corps')} ${pavilionName}, ${t(
                        'components.building.rightMenu.section'
                      )} ${sectionName}, ${getFloorName(room.data.floorName)}`
                    : getFloorName(room.data.floorName)}
                </div>
                <div className={styles.PrintHeaderObjectName}>{this.props.building.data.title}</div>
              </div>

              <img src={logoPrint} alt="" className={styles.LogoPrint} />
            </div>
          )}
          <span className={cn(styles.FloorNumber, { [styles.FloorNumberPrint]: print })}></span>
          {/* <AngleDown className={styles.MoveRight} /> */}
        </div>
        <div className={cn(styles.ImageOuter, styles.ImageWrap)}>
          <SpinnerImage
            key={room.id}
            className={cn(styles.ImageInner, { [styles.ImagePrint]: print })}
            imageSrc={`${ROOT_URL}/image/plan?id=${room.planId}&preset=${print ? '900' : '300'}`}
            imageAlt="Floor preview"
            handleImageLoad={this.handleImageLoad}
            handleImageError={this.props.handleImageError}
            spinner={<div className={styles.Loader} />}
            placeholder={
              <div className={styles.PlaceholderOuter}>
                <img className={styles.PlaceholderImage} src={noFloor} alt="Not found" />
                <div className={styles.PlaceholderText}>
                  {t('components.building.rightMenu.emptyPlanning')}
                </div>
              </div>
            }
          >
            {this.renderVectorOverlay()}
            {isLoaded ? (
              <div className={cn(styles.Zoom)} onClick={this.setPopUp}>
                <img
                  alt="zoom"
                  src={zoomIcon}
                  data-tip={t('components.building.rightMenu.zoomImg')}
                  data-for="global"
                />
              </div>
            ) : null}
          </SpinnerImage>
        </div>
        {
          <Modal
            isOpen={this.state.showPopup}
            onRequestClose={this.handleClose}
            contentLabel="Modal"
            style={{
              overlay: {
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
              },
              content: {
                padding: 0,
                border: 'none',
              },
            }}
          >
            <ChessGalleryPopupWithRouter {...this.props} onClose={this.handleClose} />
          </Modal>
        }
      </div>
    );
  }
}

const gallerySelector = createSelector(
  (state) => state.profile,
  (profile) => ({
    currency: profile.user_region.country_currency_symbol,
  })
);

export default withTranslation()(connect(gallerySelector, { setPopUp })(Gallery));



// WEBPACK FOOTER //
// ./src/components/Building/Chess/RightMenu/Gallery/Gallery.js